import type { IContractRepository } from "@/application/ports/repositories";
import type {
  ContractListResponse,
  ContractListParams,
  ContractStatsParams,
  ContractStatsResponse,
  ResendDocumentResponse,
  VoidContractResponse,
} from "@/domain/contract";
import type { DocumentDownloadResponse } from "@/domain/documentBlocks";
import { downloadFromSignedUrl } from "../fileDownload";
import * as sdk from "../generated/sdk.gen";
import { unwrap } from "../unwrap";

export class ContractRepository implements IContractRepository {
  async listContracts(params: ContractListParams = {}): Promise<ContractListResponse> {
    const { items, ...rest } = await unwrap(sdk.listContracts({ query: params }));
    return { contracts: items, ...rest };
  }

  async getContractStats(params: ContractStatsParams = {}): Promise<ContractStatsResponse> {
    return unwrap(sdk.getContractStats({ query: params }));
  }

  async getContractDocument(contractUuid: string): Promise<DocumentDownloadResponse> {
    return unwrap(sdk.getContractDocument({ path: { contract_uuid: contractUuid } }));
  }

  async downloadContractDocument(contractUuid: string): Promise<void> {
    const documentResponse = await this.getContractDocument(contractUuid);
    await downloadFromSignedUrl(documentResponse.signed_url, `contract-${contractUuid}.pdf`);
  }

  async resendContract(
    investmentUuid: string,
    documentUuid: string,
  ): Promise<ResendDocumentResponse> {
    return unwrap(
      sdk.resendDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }

  async voidContract(investmentUuid: string, documentUuid: string): Promise<VoidContractResponse> {
    return unwrap(
      sdk.voidContract({
        path: { investment_uuid: investmentUuid },
        body: { document_uuid: documentUuid },
      }),
    );
  }

  async deleteContract(investmentUuid: string, documentUuid: string): Promise<void> {
    await unwrap(
      sdk.deleteInvestmentDocument({
        path: { investment_uuid: investmentUuid, document_uuid: documentUuid },
      }),
    );
  }
}
